import { awaitableTemplate } from '../../../../webmodule-common/other/ui/template-processor';
import { currencyCodeToString, moneyToTemplateResult } from '../../../../webmodule-common/other/currency-formatter';
import { customElement, property } from 'lit/decorators.js';
import { DashboardCache } from '../data/dashboard-cache';
import { DashboardWidget } from './dashboard-widget';

import { getApiFactory } from '../../../api/api-injector';
import { glossary } from '../../../../localization/global';
import { html } from 'lit';
import { LangDashboardBranchMonthlyRevenueByState } from '../../../../localization/scripts/dealer-franchisee/dashboard/widget/branch-monthly-revenue-bystate-lang';
import { PromiseSnippet } from '../../../../webmodule-common/interop/webmodule-interop';
import { QuoteState, ViewQuoteTotalByState } from '../../../api/dealer-api-interface-quote';
import { Snippet } from '../../../../webmodule-common/other/ui/events';

@customElement('dashboard-branch-monthly-revenue-bystate')
export class BranchMonthlyRevenueByState extends DashboardWidget {
  api = getApiFactory().quote();
  @property() dashboardCache: DashboardCache;

  @property() assignedToUserId?: string;
  @property() state = 0;

  protected async getData(): Promise<ViewQuoteTotalByState[]> {
    const state = this.state;
    return await this.dashboardCache.getMonthlyQuoteFinancials(state, this.assignedToUserId);
  }

  protected getHeader(): Snippet {
    return LangDashboardBranchMonthlyRevenueByState.monthlyGeneratedRevenue({ state: QuoteState[this.state] });
  }

  protected getQuoteLabel(): Snippet {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    if (this.state >= QuoteState.Approved) return glossary('order:plural', 'UC');
    return glossary('quote:plural', 'UC');
  }

  protected getBody(): Snippet {
    return html`${awaitableTemplate(this.getTemplate(), 'Loading...')}`;
  }

  protected async getTemplate(): PromiseSnippet {
    const data = await this.getData();

    // Group by currency (enum numeric). We'll convert to ISO strings for display/formatting
    const groups = new Map<number, ViewQuoteTotalByState[]>();
    data?.forEach(item => {
      const key = item.currency;
      const list = groups.get(key);
      if (list) list.push(item);
      else groups.set(key, [item]);
    });

    // Build sections per currency
    const sections: Snippet[] = [];
    groups.forEach((items, currencyEnum) => {
      const quoteCount = items.reduce((acc, x) => acc + (x.quoteCount ?? 0), 0);
      const total = items.reduce((acc, x) => acc + (x.total ?? 0), 0);
      const currencyIso = currencyCodeToString(currencyEnum) ?? 'USD';
      sections.push(html`
        <div class="mb-3">
          <div class="card-title fw-semibold">${currencyIso}</div>
          <div class="card-subtitle text-secondary">${quoteCount} ${this.getQuoteLabel()}</div>
          <div class="display-5 text-primary">${moneyToTemplateResult(total, currencyEnum)}</div>
        </div>
      `);
    });

    // If no data, show zero with default currency formatting
    if (sections.length === 0) {
      return html`
        <div class="card-subtitle text-secondary">0 ${this.getQuoteLabel()}</div>
        <div class="display-5 text-primary"></div>
      `;
    }

    return html`${sections}`;
  }
}
